import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resolveChatUploadMediaType } from "../utils/chat-upload-media-type";
/** Drafts that hold (or are on their way to holding) one of the per-turn slots. */ function occupiesSlot(draft) {
    return draft.status !== "error";
}
export function useChatAttachmentDrafts(options) {
    const [drafts, setDrafts] = useState([]);
    // Mirrored because uploads resolve out of band: a progress event and the
    // slot-count check both need the CURRENT list, not the one captured when the
    // handler was created.
    const draftsRef = useRef([]);
    // The caller's callbacks are typically inline lambdas, so a fresh identity
    // every render. Reading them from a ref is what keeps `addFiles`/`remove`/
    // `clear` referentially stable for the composer that spreads them.
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const write = useCallback((next)=>{
        draftsRef.current = next(draftsRef.current);
        setDrafts(draftsRef.current);
    }, []);
    const uploads = useRef(new Map());
    const patch = useCallback((id, change)=>{
        write((current)=>current.map((draft)=>draft.id === id ? {
                    ...draft,
                    ...change
                } : draft));
    }, [
        write
    ]);
    const runUpload = useCallback(async (id, file, mediaType)=>{
        const controller = new AbortController();
        uploads.current.set(id, controller);
        try {
            const { key } = await optionsRef.current.upload(file, {
                signal: controller.signal,
                onProgress: (progress)=>patch(id, {
                        progress
                    }),
                mediaType
            });
            patch(id, {
                status: "ready",
                progress: 100,
                key
            });
        } catch (error) {
            // A removed draft is gone from the list already — reporting an upload it
            // asked to cancel would resurrect it as an error chip.
            if (controller.signal.aborted) return;
            optionsRef.current.onUploadError?.(error);
            patch(id, {
                status: "error",
                rejection: optionsRef.current.uploadFailedRejection
            });
        } finally{
            uploads.current.delete(id);
        }
    }, [
        patch
    ]);
    const addFiles = useCallback((files)=>{
        for (const file of files){
            const id = crypto.randomUUID();
            const base = {
                id,
                filename: file.name,
                size: file.size,
                progress: 0
            };
            const refuse = (rejection)=>write((current)=>[
                        ...current,
                        {
                            ...base,
                            status: "error",
                            rejection
                        }
                    ]);
            const mediaType = resolveChatUploadMediaType(file);
            if (!mediaType) {
                refuse(optionsRef.current.unsupportedTypeRejection);
                continue;
            }
            const rejection = optionsRef.current.reject(file, draftsRef.current.filter(occupiesSlot).length);
            if (rejection) {
                refuse(rejection);
                continue;
            }
            write((current)=>[
                    ...current,
                    {
                        ...base,
                        status: "uploading",
                        mediaType
                    }
                ]);
            void runUpload(id, file, mediaType);
        }
    }, [
        runUpload,
        write
    ]);
    const remove = useCallback((id)=>{
        uploads.current.get(id)?.abort();
        uploads.current.delete(id);
        write((current)=>current.filter((draft)=>draft.id !== id));
    }, [
        write
    ]);
    const abortAllUploads = useCallback(()=>{
        uploads.current.forEach((controller)=>controller.abort());
        uploads.current.clear();
    }, []);
    const clear = useCallback(()=>{
        abortAllUploads();
        write(()=>[]);
    }, [
        abortAllUploads,
        write
    ]);
    // Drafts belong to ONE conversation: switching sessions must not carry a
    // readied file across.
    // The cleanup runs when the key changes away from an id (`id → other id`,
    // `id → undefined` = the host asking for a new chat). `undefined → id` is a
    // brand-new chat learning its own id when its first answer finishes — the
    // SAME conversation, and aborting the file attached while that answer
    // streamed would drop it without even an error chip, so it registers nothing.
    const { sessionKey } = options;
    useEffect(function dropDraftsWhenLeavingTheSession() {
        if (sessionKey === undefined) return;
        return clear;
    }, [
        sessionKey,
        clear
    ]);
    // The callback identity is stable and the Map it reads never changes identity
    // either, so this aborts whatever is in flight at UNMOUNT, not what is in
    // flight now.
    useEffect(function abortUploadsOnUnmount() {
        return abortAllUploads;
    }, [
        abortAllUploads
    ]);
    const attachments = useMemo(()=>drafts// A draft only gets a key by uploading, and only a draft this hook
        // classified uploads — so a keyed draft always carries its media type.
        .filter((draft)=>Boolean(draft.key)).map((draft)=>({
                key: draft.key,
                filename: draft.filename,
                mediaType: draft.mediaType,
                size: draft.size
            })), [
        drafts
    ]);
    return {
        drafts,
        attachments,
        addFiles,
        remove,
        clear,
        isUploading: drafts.some((draft)=>draft.status === "uploading")
    };
}

//# sourceMappingURL=useChatAttachmentDrafts.js.map